"use client";

import { create } from "zustand";
import type { HarnessId } from "@bash/shared";

/**
 * Which HARNESS (agent engine) drives the NEXT task. There is exactly one: the
 * native (Vestra) multi-model harness. The claude and openai arms were deleted
 * (2026-08-12); the two-pane engine picker was already removed in the 2026-07-10
 * redesign, so nothing writes a non-native value any more.
 *
 * `runtime` still rides the USER_MESSAGE payload for wire compatibility, and the
 * server coerces whatever arrives to native anyway (coerceCreationRuntime /
 * resolveLaunchRuntime).
 *
 * loadRuntime deliberately IGNORES the persisted value. Browsers that stored
 * 'claude' or 'openai' before the picker was removed would otherwise keep
 * sending a legacy id on every new task — the last surface still minting one.
 * The key is cleared on read so the stale value stops shadowing the default.
 */
const STORAGE_KEY = "bash-agent-runtime";
const DEFAULT_RUNTIME: HarnessId = "native";

function loadRuntime(): HarnessId {
  if (typeof window === "undefined") return DEFAULT_RUNTIME;
  if (
    localStorage.getItem(STORAGE_KEY) !== null &&
    localStorage.getItem(STORAGE_KEY) !== DEFAULT_RUNTIME
  ) {
    localStorage.removeItem(STORAGE_KEY);
  }
  return DEFAULT_RUNTIME;
}

interface RuntimeState {
  runtime: HarnessId;
  setRuntime: (runtime: HarnessId) => void;
}

export const useRuntimeStore = create<RuntimeState>((set) => ({
  runtime: loadRuntime(),
  setRuntime: (runtime) => {
    localStorage.setItem(STORAGE_KEY, runtime);
    set({ runtime });
  },
}));
